import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './ToTheMoonProject.css';

gsap.registerPlugin(ScrollTrigger);

const APP = '/content/tothemoon/app/dist/';

/**
 * An iframe rendered at its true size and scaled to its column. The prototype
 * lays out at fixed dimensions, so scaling rather than resizing keeps the camera
 * moves and type proportioned at any width.
 */
const ScaledFrame: React.FC<{
  src: string; title: string; width: number; height: number; className?: string;
}> = ({ src, title, width, height, className }) => {
  const box = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0);

  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setScale(e.contentRect.width / width));
    ro.observe(el);
    return () => ro.disconnect();
  }, [width]);

  return (
    <div ref={box} className={className} style={{ height: scale ? height * scale : undefined }}>
      {scale > 0 && (
        <iframe
          src={src} title={title} loading="lazy"
          style={{ width, height, border: 0, display: 'block', transformOrigin: 'top left', transform: `scale(${scale})` }}
        />
      )}
    </div>
  );
};

/** Three screens that look different at a glance: a chart, a list, and a record. */
const heroScreens = [
  { screen: 'detail', extra: '&chart=candles', label: 'Candle chart' },
  { screen: 'home', extra: '', label: 'Home' },
  { screen: 'activity', extra: '', label: 'Activity' },
];

const meta = [
  { label: 'Type', value: 'Product Design' },
  { label: 'Year', value: '2026' },
  { label: 'Role', value: 'Research, Design, Build' },
  { label: 'Scope', value: 'Mobile and Desktop' },
];

const outcomes = [
  { title: 'One product at two densities', desc: 'The order ticket carries the same power in two forms, switched in place, so nothing has to be hidden permanently.' },
  { title: 'Density follows the device', desc: 'Focused is the default because entering numbers under time pressure is a documented failure mode on phones. It is a constraint of the device.' },
  { title: 'The language got audited too', desc: 'The research had settled how much to show and never what to call it. Net liq became portfolio value.' },
  { title: 'Order state made explicit', desc: 'Why an order has not filled, the spread before you trade, and the fill price and venue afterwards.' },
];

const findings = [
  { stat: '22%', text: 'of customers used more than one brokerage in 2023, up from 15% in 2019. People are keeping two accounts rather than replacing one.' },
  { stat: '34 / 659', text: 'watchlist columns at Robinhood against Interactive Brokers. This design had to sit somewhere between the two.' },
  { stat: '$7.5M', text: 'settled in January 2024, after the Massachusetts Securities Division named confetti animations, scratch tickets and free-stock rewards in its 2020 complaint. Academic work separately finds heavy gamification lowers the perceived seriousness of the institution running it.' },
  { stat: 'Mar 2020', text: 'Robinhood went down across high-volatility trading days, locking people out of exiting positions. Unstable connection is also the most-cited drawback of trading on a phone.' },
];

/** The category's documented failures, in order, ending on the anchor fact. */
const timeline = [
  { when: 'Mar 2020', what: 'Robinhood goes down across high-volatility trading days. People cannot exit positions.' },
  { when: 'Dec 2020', what: 'Massachusetts Securities Division names confetti, scratch tickets and free-stock rewards as gamification.' },
  { when: 'Jan 2024', what: 'Settled for $7.5M, with mandated changes to its practices.' },
  { when: 'Oct 2024', what: 'Robinhood launches Legend, a separate desktop platform for active traders.' },
  { when: 'Jun 2025', what: 'Legend\u2019s advanced charts arrive on the phone: "until now, only available on desktop".' },
];

const ladder = [
  { name: 'GlobalTrader', pos: 'Simple mobile trading for novice investors', level: 1 },
  { name: 'IBKR Desktop', pos: 'Balances simplicity with advanced functionality', level: 2 },
  { name: 'IBKR Mobile', pos: 'Flagship mobile, full power', level: 3 },
  { name: 'Trader Workstation', pos: 'Pro and institutional', level: 4 },
];

const references = [
  {
    name: 'Interactive Brokers',
    what: 'The professional platform. Ships the same power as four products at four densities.',
    pairs: [
      {
        trait: 'The simplest order ticket has a one-tap switch to the full one.',
        result: 'The Focused and Advanced ticket. This is where the project started.',
      },
      {
        trait: 'Density treated as a feature. Real terminology, unsimplified.',
        result: 'Kept for the desktop frame, which carries bid, ask and volume columns a phone cannot fit. Rejected for the vocabulary, which stayed plain on both.',
      },
      {
        trait: 'Security shown as interface rather than buried in settings.',
        result: 'The account screen states what each control actually protects rather than saying On.',
      },
    ],
  },
  {
    name: 'Robinhood',
    what: 'The simple end of the category, and the one with a regulator settlement attached.',
    pairs: [
      {
        trait: 'The home screen is a large portfolio value with a scrubbable chart under it.',
        result: 'Taken almost unchanged. An earlier build had the number without a chart, which was simply less useful.',
      },
      {
        trait: 'Confetti on a filled order, named in the Massachusetts complaint.',
        result: 'Reversed. The confirmation became a receipt: fill price, best bid and ask at the moment it filled, and a reference.',
      },
    ],
  },
];

const tokens = [
  {
    group: 'Ground and surfaces',
    note: 'Neutral grey. Close to black, never black.',
    items: [
      ['#161616', 'ground', 'App base'],
      ['#1E1E1E', 'surface', 'Panels, headers'],
      ['#282828', 'raised', 'Active states, sheets'],
      ['#2E2E2E', 'rule', 'Every divider'],
      ['#3E3E3E', 'rule-bright', 'Emphasised edges'],
    ],
  },
  {
    group: 'Type',
    note: 'Three steps, because a fourth would not be distinguishable at these sizes.',
    items: [
      ['#EBEBEB', 'ink', 'Values, headings'],
      ['#888888', 'muted', 'Labels, secondary'],
      ['#707070', 'faint', 'Timestamps, hints'],
    ],
  },
  {
    group: 'Semantic',
    note: 'Used for profit and loss direction only.',
    items: [
      ['#26C281', 'pos', 'Gains'],
      ['#124534', 'pos-dim', 'Gain fills'],
      ['#EF4444', 'neg', 'Losses'],
      ['#461B1B', 'neg-dim', 'Loss fills'],
    ],
  },
  {
    group: 'Accent',
    note: 'Marks an action you can take, or a state that wants attention. Never decoration.',
    items: [
      ['#E4572E', 'accent', 'Trade, working, active tab'],
      ['#552112', 'accent-dim', 'Accent backgrounds'],
    ],
  },
];

const typeScale = [
  ['hero', '40 / 40', '-0.025em', 'Portfolio value, price'],
  ['figure', '26', '0', 'The big number in a block'],
  ['wordmark', '16 / 16', '-0.03em', 'The wordmark, nothing else'],
  ['row', '15 / 18', '0', 'Ticker, list values'],
  ['data', '13 / 16', '0', 'Body, stats'],
  ['caption', '11 / 14', '0', 'Company names, notes'],
  ['micro', '10 / 12', '0.09em', 'Section labels, uppercase'],
  ['badge', '9', '0', 'Status badges, column heads'],
];

const easings = [
  ['ease-out', 'cubic-bezier(0.23, 1, 0.32, 1)', 'Almost everything. Fast out, settles slowly.'],
  ['ease-drawer', 'cubic-bezier(0.32, 0.72, 0, 1)', 'Sheets, and the Advanced expansion.'],
];

const reels = [
  { id: 'density', name: 'The order form grows instead of moving you', body: 'Tapping Advanced expands the same form in place, adding order type, limit price and how long the order stays live. Nothing opens on top of it and nothing hides in a settings screen. It starts simple because typing numbers in a hurry is where phone trading goes wrong, which is a fact about the device rather than about the person holding it.' },
  { id: 'candles', name: 'Both charts, one tap apart', body: 'Simple apps show a line and leave candles to a desktop platform. Robinhood shipped its advanced charts on desktop in October 2024 and did not bring them to the phone until June 2025. A candle is not a desktop feature. It is the same prices with the open and close kept, so both live on this screen and switching is one tap.' },
  { id: 'alert', name: 'Drag a line to set a price alert', body: 'You drag a line to the price you care about and the app works out the rest: above if you leave it over the current price, below if you leave it under. There is no Above or Below menu, because that would make you describe in words what you just did with your finger. The line sticks to the day high, the open and the previous close.' },
  { id: 'dollars', name: 'Buy $500 of it, not 2.76 shares', body: 'People decide how much money to put in, not how many shares to end up holding. Shares-only entry also shuts out anyone whose budget is smaller than a single share of an expensive company. Type either number and the other one stays on screen, so you always see both.' },
  { id: 'why', name: 'It tells you why an order has not filled', body: 'The most common complaint about order screens is not knowing why nothing has happened yet. The largest thing on this one is the answer, in plain language, with the exact gap the price still has to close. Support opens already knowing which order you mean.' },
];

const corrections = [
  { title: 'The research had never argued about language', body: 'Auditing the app against Robinhood gave a sharper answer than too dense or not dense enough. Almost every density decision had evidence behind it, and not one naming decision did. The biggest number on the home screen was labelled Net liq. The spread was quoted in basis points. The rule that came out of it: show what the research supports, and name it the way this user would say it.' },
  { title: 'Three badges were covering a filing error', body: 'A working order sat inside a list called Positions and needed a tinted row, a bordered badge and a second label to look different from its neighbours. It is not a position. Once orders and holdings were separated, none of those treatments were needed. The badges had been compensating for a filing mistake.' },
  { title: 'A fact-check found a fabricated quote in my own research', body: 'A positioning quote attributed to one of the reference products appears nowhere on its site. It had come from an AI summary of the page rather than the page itself, along with two wrong product claims, and it had already been written into the docs in quotation marks. Everything sourced from raw pages and primary filings held up. The research now cites the regulator filing and the companies own published files rather than write-ups of them, which matters when the research is the argument.' },
];

interface Props { theme?: string; }

const ToTheMoonProject: React.FC<Props> = () => {
  const rootRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    const prev = document.body.className;
    document.body.className = 'light';
    return () => { document.body.className = prev; };
  }, []);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from('.ttm__hero-frame', { y: 70, opacity: 0, duration: 1.5, ease: 'power3.out', delay: 0.1 });
      gsap.from('.ttm__title', { y: 32, opacity: 0, duration: 0.9, ease: 'power3.out', delay: 0.25 });
      gsap.from('.ttm__descriptor', { y: 22, opacity: 0, duration: 0.8, ease: 'power3.out', delay: 0.4 });
      gsap.from('.ttm__meta-item', { y: 16, opacity: 0, duration: 0.65, ease: 'power3.out', stagger: 0.07, delay: 0.55 });

      gsap.set('.ttm-fade-up', { y: 30, opacity: 0 });
      gsap.set('.ttm-fade-scale', { scale: 0.93, opacity: 0 });

      ScrollTrigger.batch('.ttm-fade-up', {
        onEnter: (els) => gsap.to(els, { y: 0, opacity: 1, stagger: 0.09, duration: 0.85, ease: 'power3.out' }),
        start: 'top 90%', once: true,
      });
      ScrollTrigger.batch('.ttm-fade-scale', {
        onEnter: (els) => gsap.to(els, { scale: 1, opacity: 1, stagger: 0.08, duration: 1, ease: 'power3.out' }),
        start: 'top 88%', once: true,
      });
    }, rootRef);
    return () => ctx.revert();
  }, []);

  /**
   * Which reel is under the middle of the screen. The note is pinned there and
   * swaps to match, so the clip and the words about it are always the same pair.
   *
   * The margins collapse the observer's root to a zero-height line across the
   * viewport centre, so a slot counts as active exactly while it covers that
   * line. That is the same thing the eye is doing, which is why it needs no
   * thresholds or scroll maths.
   */
  const [active, setActive] = useState(0);
  const slots = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const els = slots.current.filter(Boolean) as HTMLDivElement[];
    if (!els.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(Number((e.target as HTMLElement).dataset.index));
        });
      },
      { rootMargin: '-50% 0px -50% 0px', threshold: 0 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <div className="ttm" ref={rootRef}>

      <section className="ttm__hero">
        <div className="ttm__hero-row">
          {heroScreens.map(({ screen, extra, label }) => (
            <ScaledFrame
              key={label}
              className="ttm__hero-frame"
              src={`${APP}?view=mobile&chrome=0&still=1&device=ios&screen=${screen}${extra}`}
              title={label}
              width={390} height={844}
            />
          ))}
        </div>
      </section>

      <div className="ttm__container">

        <header className="ttm__header">
          <h1 className="ttm__title">tothemoon</h1>
          <p className="ttm__descriptor">
            A mobile trading app for people who have outgrown the simple ones.
          </p>
          <div className="ttm__meta">
            {meta.map(({ label, value }) => (
              <div className="ttm__meta-item" key={label}>
                <div className="ttm__meta-label">{label}</div>
                <div className="ttm__meta-value">{value}</div>
              </div>
            ))}
          </div>
        </header>

        <section className="ttm__section">
          <div className="ttm__split">
            <div>
              <span className="ttm__tldr-tag ttm-fade-up">tl;dr</span>
              <p className="ttm__body ttm-fade-up">
                Trading apps treat simple and serious as different products. In October 2024
                Robinhood launched Legend, a separate desktop platform for active traders, rather
                than extending the app that 25 million funded accounts already used. Eight months
                later they put Legend&rsquo;s charts on the phone after all, which is the same
                conclusion reached from the other direction.
              </p>
              <p className="ttm__body ttm-fade-up">
                Nothing about a phone requires that split. The real constraints are screen size,
                connection, interruption and typing under pressure. Those are facts about the device,
                not about the person holding it. So this app changes density instead, and the same
                order ticket serves both ends.
              </p>
            </div>
            <ul className="ttm__outcomes">
              {outcomes.map(({ title, desc }) => (
                <li className="ttm__outcome ttm-fade-up" key={title}>
                  <div className="ttm__outcome-dot" />
                  <div>
                    <span className="ttm__outcome-strong">{title}</span>
                    <span className="ttm__outcome-desc">{desc}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>

      </div>

      {/* Try it. Placed before the argument, because the fastest way to
          understand a working prototype is to use it. */}
      <section className="ttm__try">
        <div className="ttm__try-inner">
          <div className="ttm__try-copy">
            <h2 className="ttm__try-head">Jump right into the prototype</h2>
            <p className="ttm__try-body">
              Eleven screens on the phone and a desktop frame, in one build. Prices move on their
              own. Set an alert by dragging it onto the chart, or take an order through to the
              receipt. The switcher at the bottom moves between the phone and the desktop.
            </p>
            <a className="ttm__try-cta" href={`${APP}?back=/projects/tothemoon`} target="_blank" rel="noreferrer">
              Open in a new tab
              <span className="ttm__try-arrow" aria-hidden="true">→</span>
            </a>
          </div>

          <div className="ttm__try-stage">
            <ScaledFrame
              className="ttm__try-frame"
              src={`${APP}?view=mobile&chrome=0`}
              title="tothemoon, running"
              width={390} height={844}
            />
          </div>
        </div>
      </section>

      {/* Interactions */}
      <section className="ttm__reels-section">
        <div className="ttm__reels-inner">
          <div className="ttm__section-label ttm-fade-up">interactions</div>

          {/* Clip and note are siblings in one grid rather than a row each. On
              wide screens every note is given the same full-height column, so
              each can pin at the centre for the whole section and the active one
              is the only one shown. Narrow screens drop the placement and the
              source order puts each note back under its own clip, which is why
              the notes are not duplicated for the two layouts. */}
          <div className="ttm__reels-grid">
            {reels.map(({ id, name, body }, i) => (
              <React.Fragment key={id}>
                <div
                  className="ttm__reel-slot"
                  data-index={i}
                  ref={(el) => { slots.current[i] = el; }}
                >
                  <ScaledFrame
                    className="ttm__reel-frame ttm-fade-scale"
                    src={`${APP}?view=reels&chrome=0&reel=${id}`}
                    title={name} width={1600} height={900}
                  />
                </div>

                {/* No ttm-fade-up here: GSAP reveals it by writing inline opacity
                    and transform, which is exactly what the swap and the centring
                    need to control. */}
                <div className={i === active ? 'ttm__note ttm__note--on' : 'ttm__note'}>
                  <div className="ttm__reel-num">{String(i + 1).padStart(2, '0')}</div>
                  <div className="ttm__reel-title">{name}</div>
                  <p className="ttm__reel-desc">{body}</p>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      <div className="ttm__container">
        {/* The system */}
        <section className="ttm__section">
          <div className="ttm__section-label ttm-fade-up">the system</div>
          <div className="ttm__split">
            <p className="ttm__statement ttm-fade-up">
              Four rules, and each one is a limit.
            </p>
            <p className="ttm__body ttm-fade-up">
              Mono means a number. Green means profit or loss. The accent means you can tap it.
              Each one works only because nothing else is allowed to use it.
            </p>
          </div>

          <div className="ttm__tokens ttm-fade-up">
            {tokens.map(({ group, note, items }) => (
              <div className="ttm__token-group" key={group}>
                <div className="ttm__token-head">
                  <span className="ttm__token-group-name">{group}</span>
                  <span className="ttm__token-note">{note}</span>
                </div>
                <div className="ttm__token-rows">
                  {items.map(([hex, name, use]) => (
                    <div className="ttm__token" key={name}>
                      <span className="ttm__token-chip" style={{ background: hex }} />
                      <span className="ttm__token-name">{name}</span>
                      <span className="ttm__token-hex">{hex}</span>
                      <span className="ttm__token-use">{use}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="ttm__scales ttm-fade-up">
            <div className="ttm__scale">
              <div className="ttm__scale-title">Type scale</div>
              <div className="ttm__scale-rows">
                {typeScale.map(([name, size, tracking, use]) => (
                  <div className="ttm__scale-row" key={name}>
                    <span className="ttm__scale-name ttm__mono">{name}</span>
                    <span className="ttm__scale-size ttm__mono">{size}</span>
                    <span className="ttm__scale-track ttm__mono">{tracking}</span>
                    <span className="ttm__scale-use">{use}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="ttm__scale">
              <div className="ttm__scale-title">Motion</div>
              <div className="ttm__scale-rows">
                {easings.map(([name, curve, use]) => (
                  <div className="ttm__scale-row ttm__scale-row--ease" key={name}>
                    <span className="ttm__scale-name ttm__mono">{name}</span>
                    <span className="ttm__scale-curve ttm__mono">{curve}</span>
                    <span className="ttm__scale-use">{use}</span>
                  </div>
                ))}
              </div>
              <p className="ttm__scale-foot">
                Three curves for the whole app.
              </p>
            </div>
          </div>

          <div className="ttm__rules">

            <div className="ttm__rule ttm-fade-up">
              <div className="ttm__rule-num">01</div>
              <div className="ttm__rule-body">
                <div className="ttm__rule-title">Mono marks data. Everything else is Geist.</div>
                <p className="ttm__rule-desc">
                  The first build set IBM Plex Mono on prices, tickers, nav, buttons and section
                  labels. Mono is meant to mark a figure you read precisely. When it is also the tab
                  bar it marks nothing.
                </p>
                <div className="ttm__demo">
                  <div className="ttm__demo-col">
                    <div className="ttm__demo-label">Everything mono</div>
                    <div className="ttm__row ttm__row--allmono">
                      <span className="ttm__row-sym">NVDA</span>
                      <span className="ttm__row-name">NVIDIA Corp</span>
                      <span className="ttm__row-px">181.06</span>
                    </div>
                  </div>
                  <div className="ttm__demo-col">
                    <div className="ttm__demo-label">Split</div>
                    <div className="ttm__row">
                      <span className="ttm__row-sym ttm__mono">NVDA</span>
                      <span className="ttm__row-name">NVIDIA Corp</span>
                      <span className="ttm__row-px ttm__mono">181.06</span>
                    </div>
                  </div>
                </div>
                <div className="ttm__rule-test">
                  The test: if you would compare it to the value above it, it is mono. If you would
                  read it as a word, it is Geist. Tickers count as data.
                </div>
              </div>
            </div>

            <div className="ttm__rule ttm-fade-up">
              <div className="ttm__rule-num">02</div>
              <div className="ttm__rule-body">
                <div className="ttm__rule-title">Green and red mean profit and loss. Nothing else.</div>
                <p className="ttm__rule-desc">
                  The category convention is green for buy and red for sell. Those two colours
                  already carry profit and loss here, so using them for order side as well would put
                  four meanings on two colours. Buy takes the accent and Sell is outlined.
                </p>
                <div className="ttm__demo">
                  <div className="ttm__demo-col ttm__demo-col--wide">
                    <div className="ttm__demo-label">Buy and sell</div>
                    <div className="ttm__btns">
                      <span className="ttm__btn ttm__btn--out">Sell</span>
                      <span className="ttm__btn ttm__btn--accent">Buy</span>
                    </div>
                  </div>
                  <div className="ttm__demo-col ttm__demo-col--wide">
                    <div className="ttm__demo-label">Reserved for direction</div>
                    <div className="ttm__pnl">
                      <span className="ttm__pnl-up">+$2,298.30</span>
                      <span className="ttm__pnl-down">−$523.20</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="ttm__rule ttm-fade-up">
              <div className="ttm__rule-num">03</div>
              <div className="ttm__rule-body">
                <div className="ttm__rule-title">Hairlines carry the structure. No cards.</div>
                <p className="ttm__rule-desc">
                  No radius above 2px, no shadows, no gradients, no fills to separate one thing from
                  another. A row is defined by the line under it, which keeps dense data readable
                  without the screen filling with containers.
                </p>
                <div className="ttm__demo ttm__demo--stack">
                  <div className="ttm__row"><span className="ttm__row-sym ttm__mono">TSLA</span><span className="ttm__row-name">Tesla Inc</span><span className="ttm__row-px ttm__mono">404.18</span></div>
                  <div className="ttm__row"><span className="ttm__row-sym ttm__mono">AAPL</span><span className="ttm__row-name">Apple Inc</span><span className="ttm__row-px ttm__mono">263.95</span></div>
                  <div className="ttm__row"><span className="ttm__row-sym ttm__mono">SPY</span><span className="ttm__row-name">SPDR S&amp;P 500 ETF</span><span className="ttm__row-px ttm__mono">694.72</span></div>
                </div>
              </div>
            </div>

            <div className="ttm__rule ttm-fade-up">
              <div className="ttm__rule-num">04</div>
              <div className="ttm__rule-body">
                <div className="ttm__rule-title">The accent marks an action, or a state that wants you.</div>
                <p className="ttm__rule-desc">
                  Never decoration, and never data. An accent used everywhere marks nothing, the
                  same failure the mono had.
                </p>
                <div className="ttm__accent-list">
                  <span className="ttm__accent-item"><span className="ttm__accent-dot" /> Trade action</span>
                  <span className="ttm__accent-item"><span className="ttm__accent-dot" /> Order not yet filled</span>
                  <span className="ttm__accent-item"><span className="ttm__accent-dot" /> Active tab</span>
                  <span className="ttm__accent-item"><span className="ttm__accent-dot" /> Alert level being set</span>
                </div>
              </div>
            </div>

          </div>

          <div className="ttm__amend ttm-fade-up">
            <span className="ttm__amend-label">Amendment</span>
            <p>
              The type spec originally called for one family everywhere, IBM Plex Sans and its
              matching mono. It failed once the app was built and rule 01 replaced it. The docs
              carry both versions.
            </p>
          </div>
        </section>
      </div>

      <div className="ttm__container">

        {/* Research */}
        <section className="ttm__section">
          <div className="ttm__section-label ttm-fade-up">research</div>
          <div className="ttm__split">
            <p className="ttm__statement ttm-fade-up">
              The failures in this category are documented, and several have regulators attached.
            </p>
            <div>
              <ul className="ttm__findings">
                {findings.slice(0, 2).map(({ stat, text }) => (
                  <li className="ttm__finding ttm-fade-up" key={stat}>
                    <span className="ttm__finding-stat">{stat}</span>
                    <span className="ttm__finding-text">{text}</span>
                  </li>
                ))}
              </ul>
              {/* 34 against 659, drawn to scale. The ratio is the argument. */}
              <div className="ttm__cols ttm-fade-up">
                <div className="ttm__cols-title">Watchlist columns available</div>
                <div className="ttm__cols-row">
                  <span className="ttm__cols-name">Robinhood</span>
                  <span className="ttm__cols-bar"><i style={{ width: `${(34 / 659) * 100}%` }} /></span>
                  <span className="ttm__cols-val">34</span>
                </div>
                <div className="ttm__cols-row">
                  <span className="ttm__cols-name">Interactive Brokers</span>
                  <span className="ttm__cols-bar"><i style={{ width: '100%' }} /></span>
                  <span className="ttm__cols-val">659</span>
                </div>
                <div className="ttm__cols-row ttm__cols-row--ours">
                  <span className="ttm__cols-name">tothemoon</span>
                  <span className="ttm__cols-bar"><i style={{ width: `${(5 / 659) * 100}%` }} /></span>
                  <span className="ttm__cols-val">5</span>
                </div>
                <p className="ttm__cols-note">
                  Three columns on the phone, five on desktop, and none of them configurable. The
                  answer to 659 options is not 330 of them, it is choosing well and letting people
                  sort.
                </p>
              </div>

              <p className="ttm__caveat ttm-fade-up">
                No primary research. The user this is designed for, an active retail trader who
                trades on their phone by choice, is derived from the figures above: people are
                keeping two brokerages rather than replacing one, and the cohort that entered the
                market through mobile apps never had a desktop platform to graduate back to.
              </p>
            </div>
          </div>

          <div className="ttm__timeline">
            {timeline.map(({ when, what }, i) => (
              <div className="ttm__tl-item ttm-fade-up" key={when}>
                <div className="ttm__tl-marker" data-last={i === timeline.length - 1 || undefined} />
                <div className="ttm__tl-when">{when}</div>
                <p className="ttm__tl-what">{what}</p>
              </div>
            ))}
          </div>

          <div className="ttm__answers ttm-fade-up">
            <div className="ttm__answers-label">What each one asked for</div>
            <div className="ttm__answers-grid">
              {[
                ['The outage', 'The app is never silent about its own state. Connection is shown the moment it degrades, and no order sits in a state you cannot name.'],
                ['The complaint', 'The confirmation is a receipt rather than a celebration. No confetti, no rewards, nothing that treats a trade as a win.'],
                ['The settlement', 'Fill price, venue and the best bid and ask at the time, so the evidence that a fill was fair is on the screen.'],
                ['Legend', 'One product at two densities instead of two products, which is the whole build.'],
              ].map(([k, v]) => (
                <div className="ttm__answer" key={k}>
                  <div className="ttm__answer-k">{k}</div>
                  <p className="ttm__answer-v">{v}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>

      {/* References: what was taken, not what was looked at */}
      <section className="ttm__refs-section">
        <div className="ttm__container">
          <div className="ttm__section-label ttm-fade-up">references</div>
          <div className="ttm__split">
            <p className="ttm__statement ttm-fade-up">
              Two references, used for different things.
            </p>
            <p className="ttm__body ttm-fade-up">
              One supplied the mechanism the whole project rests on. The other supplied the shape of
              a phone screen, and one thing worth reversing.
            </p>
          </div>

          <div className="ttm__refs">
            {references.map(({ name, what, pairs }) => (
              <div className="ttm__ref-group" key={name}>
                <div className="ttm__ref-head ttm-fade-up">
                  <div className="ttm__ref-name">{name}</div>
                  <p className="ttm__ref-what">{what}</p>
                </div>
                <div className="ttm__ref-pairs">
                  {pairs.map(({ trait, result }) => (
                    <div className="ttm__pair ttm-fade-up" key={trait}>
                      <p className="ttm__pair-trait">{trait}</p>
                      <p className="ttm__pair-result">{result}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="ttm__ladder ttm-fade-up">
            <div className="ttm__ladder-label">
              IBKR does not ship one app. It ships the same power at four densities.
            </div>
            <div className="ttm__steps">
              {ladder.map(({ name, pos, level }) => (
                <div className="ttm__step" key={name}>
                  <div className="ttm__step-bars">
                    {[1, 2, 3, 4].map((n) => (
                      <span key={n} className={n <= level ? 'ttm__step-bar ttm__step-bar--on' : 'ttm__step-bar'} />
                    ))}
                  </div>
                  <div className="ttm__step-name">{name}</div>
                  <div className="ttm__step-pos">{pos}</div>
                </div>
              ))}
            </div>

            <div className="ttm__ours ttm-fade-up">
              <div className="ttm__ours-bars">
                <span className="ttm__ours-bar ttm__ours-bar--on">Focused</span>
                <span className="ttm__ours-span" />
                <span className="ttm__ours-bar ttm__ours-bar--on">Advanced</span>
                <span className="ttm__ours-bar" />
                <span className="ttm__ours-bar" />
              </div>
              <div className="ttm__ours-name">tothemoon</div>
              <div className="ttm__ours-pos">
                One product covering two of those points, with a tap between them. Their simplest
                order ticket already has that switch, and it is the part worth copying.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Desktop */}
      <section className="ttm__desktop-section">
        <div className="ttm__desktop-label ttm-fade-up">the other side</div>
        <p className="ttm__desktop-caption ttm-fade-up">
          One desktop frame. Every difference from the phone comes from having more room, not
          from a different user. The order ticket sits open and already advanced because nothing
          needs hiding at this size. The vocabulary is identical.
        </p>
        <ScaledFrame
          className="ttm__desktop-frame ttm-fade-scale"
          src={`${APP}?view=desktop&chrome=0`}
          title="tothemoon desktop frame" width={1440} height={900}
        />
      </section>

      <div className="ttm__container">
        <section className="ttm__section">
          <div className="ttm__section-label ttm-fade-up">what changed</div>
          <div className="ttm__split">
            <p className="ttm__statement ttm-fade-up">
              Three of the most useful findings were things the design got wrong first.
            </p>
            <div className="ttm__corrections">
              {corrections.map(({ title, body }) => (
                <div className="ttm__correction ttm-fade-up" key={title}>
                  <div className="ttm__correction-title">{title}</div>
                  <p className="ttm__correction-body">{body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <div className="ttm__closing">
          <p className="ttm__closing-copy ttm-fade-up">
            Eleven screens, a desktop frame and six interactions, all running in this page.
          </p>
          <a className="ttm__full-link ttm-fade-up"
            href="https://github.com/Aditya-Naik13/tothemoon"
            target="_blank" rel="noreferrer">
            Read the design decisions
          </a>
        </div>
      </div>
    </div>
  );
};

export default ToTheMoonProject;
